import type { FormInstance } from "antd";
import type {
  HighValuePurchaseRequisitionFormValue,
  LowValuePurchaseRequisitionFormValue,
  PurchaseRequestForm,
  RdRawMaterialRequisitionFormValue,
  RldReferenceProductRequisitionFormValue,
} from "../type";
import { useEffect } from "react";
import dayjs from "dayjs";
import { convertFileItems } from "@/utils/file";
import { SrmPurchaseRequestDetailVo } from "@/types/apis";
import { useUserFormFieldsQuery } from "@/hooks/services";
import { useModal } from "@/hooks/modals/useModal";
import { PurchaseRequestTypeMap } from "@/constants";
import { pickWithDefault } from "@/utils/form.helper";

export function useInitFormValues(
  form: FormInstance<PurchaseRequestForm>,
  detail?: SrmPurchaseRequestDetailVo,
) {
  const { open, props } = useModal("PURCHASE_REQUEST");
  const { dto, type } = props;
  const userInfo = useUserFormFieldsQuery(detail?.createBy);

  useEffect(() => {
    if (!open) return;

    if (detail) {
      initFormValuesFromDetail(form, detail);
    }

    if (userInfo) {
      form.setFieldsValue({
        username: userInfo.nickname,
        createDate: detail?.createTime
          ? dayjs(detail.createTime).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        employeeCode: userInfo.username,
        department: userInfo.deptName,
        postName: userInfo.postName,
        email: userInfo.email,
      });

      if (type === "HighValuePurchaseRequisition" && !detail) {
        form.setFieldsValue({
          expenseDeptName: userInfo.deptName,
          expenseDeptNo: userInfo.deptNo,
        });
      }

      if (type === "LowValuePurchaseRequisition" && !detail) {
        form.setFieldsValue({
          itemList: [{}],
        });
      }
    }

    if (dto) {
      form.setFieldsValue({
        ...dto,
      });
    }
  }, [detail, open, form, userInfo, dto, type]);

  return {
    detail,
  };
}

const initFormValuesFromDetail = (
  form: FormInstance<PurchaseRequestForm>,
  detail: SrmPurchaseRequestDetailVo,
) => {
  switch (detail.formType) {
    case PurchaseRequestTypeMap.RdRawMaterialRequisition:
      initRdRawMaterialRequisition(
        form as FormInstance<RdRawMaterialRequisitionFormValue>,
        detail,
      );
      break;
    case PurchaseRequestTypeMap.RldReferenceProductRequisition:
      initRldReferenceProductRequisition(
        form as FormInstance<RldReferenceProductRequisitionFormValue>,
        detail,
      );
      break;
    case PurchaseRequestTypeMap.HighValuePurchaseRequisition:
      initHighValuePurchaseRequisition(
        form as FormInstance<HighValuePurchaseRequisitionFormValue>,
        detail,
      );
      break;
    case PurchaseRequestTypeMap.LowValuePurchaseRequisition:
      initLowValuePurchaseRequisition(
        form as FormInstance<LowValuePurchaseRequisitionFormValue>,
        detail,
      );
      break;
    default:
      break;
  }
};

const initRdRawMaterialRequisition = (
  form: FormInstance<RdRawMaterialRequisitionFormValue>,
  detail: SrmPurchaseRequestDetailVo,
) => {
  const initFields = pickWithDefault(
    detail,
    [
      "factoryValue",
      "materialType1",
      "materialType2",
      "materialType3",
      "amountRange",
      "isCurrently",
      "materialCode",
      "materialGroup",
      "purchaseGroup",
      "materialNameCn",
      "productNameCn",
      "applicationQty",
      "applicationUnit",
      "projectNo",
      "projectName",
      "pharmacopoeia",
      "qualityStandard",
      "batchRequirements",
      "suggestedSupplierName",
      "suggestedReason",
      "requirementsRemarks",
      "isSuggestedSupplier",
      "poNo",
      "prNo",
    ],
    null,
  );

  form.setFieldsValue({
    formType: "RdRawMaterialRequisition",
    ...initFields,
    qualityStandard: convertFileItems(detail.qualityStandard),
    suggestedSupplierCoa: convertFileItems(detail.suggestedSupplierCoa),
    requirementsFiles: convertFileItems(detail.requirementsFiles),
    /** 要求完成日期 */
    deliveryDate: detail.deliveryDate ? dayjs(detail.deliveryDate) : null,
  });

  if (detail.supplierList?.length) {
    const supplier = detail.supplierList[0];
    form.setFieldsValue({
      ...supplier,
      supplierFormId: supplier.id,
      quotationFiles: convertFileItems(supplier.quotationFiles),
    });
  }
};

const initRldReferenceProductRequisition = (
  form: FormInstance<RldReferenceProductRequisitionFormValue>,
  detail: SrmPurchaseRequestDetailVo,
) => {
  const initFields = pickWithDefault(
    detail,
    [
      "materialGroup",
      "purchaseGroup",
      "factoryValue",
      "materialType1",
      "materialType2",
      "materialType3",
      "amountRange",
      "isCurrently",
      "materialNameCn",
      "materialNameEn",
      "productNameCn",
      "productNameEn",
      "isSuggestedSupplier",
      "suggestedSupplierName",
      "suggestedReason",
      "listingRegion",
      "mainIngredients",
      "spec",
      "dosageForm",
      "packagingSpec",
      "applicationQty",
      "applicationUnit",
      "projectNo",
      "batchRequirements",
      "expiryRequirements",
      "requirementsRemarks",
      "poNo",
      "prNo",
    ],
    null,
  );

  form.setFieldsValue({
    formType: "RldReferenceProductRequisition",
    ...initFields,
    suggestedSupplierCoa: convertFileItems(detail.suggestedSupplierCoa),
    requirementsFiles: convertFileItems(detail.requirementsFiles),
    deliveryDate: detail.deliveryDate ? dayjs(detail.deliveryDate) : null,
  });

  if (detail.supplierList?.length) {
    const supplier = detail.supplierList[0];
    form.setFieldsValue({
      ...supplier,
      quotationFiles: convertFileItems(supplier.quotationFiles),
    });
  }
};
const initHighValuePurchaseRequisition = (
  form: FormInstance<HighValuePurchaseRequisitionFormValue>,
  detail: SrmPurchaseRequestDetailVo,
) => {
  const initFields = pickWithDefault(
    detail,
    [
      "factoryValue",
      "materialType1",
      "materialType2",
      "materialType3",
      "amountRange",
      "isCurrently",
      "expenseDeptNo",
      "expenseDeptName",
      "costCenterNo",
      "glAccount",
      "materialGroup",
      "purchaseGroup",
      "isUrgent",
      "supplierType",
      "suggestedSupplierName",
      "suggestedReason",
      "purchaseDescription",
      "purchasePurpose",
      "budgetType",
      "projectNo",
      "budgetRemarks",
      "budgetAmt",
      "budgetAmtUnit",
      "budgetAmtHkd",
      "totalBudgetAmt",
      "totalBudgetAmtUnit",
      "totalBudgetAmtHkd",
      "poNo",
      "prNo",
      "isFixedAssets",
      "fixedAssetsNo",
    ],
    null,
  );

  form.setFieldsValue({
    formType: "HighValuePurchaseRequisition",
    ...initFields,
    meetingMinutes: convertFileItems(detail.meetingMinutes),
    attachments: convertFileItems(detail.attachments),
    complianceAssessment: convertFileItems(detail.complianceAssessment),
    deliveryDate: detail.deliveryDate ? dayjs(detail.deliveryDate) : null,
    vendorComparisonSummaryFiles: convertFileItems(
      detail.vendorComparisonSummaryFiles,
    ),
    consolidatedScoringFiles: convertFileItems(detail.consolidatedScoringFiles),
    investmentAnalysisFiles: convertFileItems(detail.investmentAnalysisFiles),
    contractFiles: convertFileItems(detail.contractFiles),
  });

  const supplierList =
    detail.supplierList && detail.supplierList.length > 0
      ? detail.supplierList?.map((supplier) => ({
          ...supplier,
          quotationFiles: convertFileItems(supplier.quotationFiles),
        }))
      : [{}];
  form.setFieldsValue({
    supplierList,
  });
};

const initLowValuePurchaseRequisition = (
  form: FormInstance<LowValuePurchaseRequisitionFormValue>,
  detail: SrmPurchaseRequestDetailVo,
) => {
  const initFields = pickWithDefault(
    detail,
    [
      "factoryValue",
      "materialType1",
      "materialType2",
      "materialType3",
      "amountRange",
      "isCurrently",
      "purchaseGroup",
      "isSuggestedSupplier",
      "suggestedSupplierName",
      "suggestedReason",
      "poNo",
      "prNo",
      "sourcingResultRemark",
      "contractRequired",
      "isFixedAssets",
      "fixedAssetsNo",
    ],
    null,
  );

  form.setFieldsValue({
    formType: "LowValuePurchaseRequisition",
    ...initFields,
    requirementsFiles: convertFileItems(detail.requirementsFiles),
    itemList: detail.itemList?.map((item) => ({
      ...item,
      deliveryDate: item.deliveryDate ? dayjs(item.deliveryDate) : null,
      attachments: convertFileItems(item.attachments),
    })),
    contractFiles: convertFileItems(detail.contractFiles),
  });

  const supplierList =
    detail.supplierList && detail.supplierList.length > 0
      ? detail.supplierList?.map((supplier) => ({
          ...supplier,
          quotationFiles: convertFileItems(supplier.quotationFiles),
        }))
      : [{}];
  form.setFieldsValue({
    supplierList,
  });
};
